import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { getFirmName } from '@deltek/specification-client/core/Widgets/ProductListing/groupedTreeUtils'
import { FirmAutoSuggestQuery } from 'bpm/api/graphQL/queries/firm.queries'
import { ProductSearchAutosuggestResult } from 'bpm/api/graphQL/types/product.types'
import { stripDoubleSpacesTrimStart } from 'bpm/shared/utils/inputUtils'
import clsx from 'clsx'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { UserType } from '../productsUtils'
import { AutoCompleteInput } from './AutoCompleteInput'
import {
    addProductDialogState,
    defaultAddProductDialogState,
    setProductDialogState,
} from './addProductFormHandler'
import { useAddProductFormStyles } from './formStyles'

const FirmNameAutoCompleteInput: FC = () => {
    const classes = useAddProductFormStyles()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [searchText, setSearchText] = useState('')

    const { firmName, errors, type } = useReactiveVar(addProductDialogState)

    //FirmAutoSuggestQuery
    const [
        firmAutoSuggest,
        { loading: firmAutoLoading, data: firmAutosuggestData },
    ] = useLazyQuery<{
        firmAutoSuggest: Partial<ProductSearchAutosuggestResult>
    }>(FirmAutoSuggestQuery, {
        onError: () => {
            setProductDialogState({
                errors: { type: errors.type, firmName: 'Something went wrong' },
            })

            showGlobalSnackbar({
                variables: {
                    message: 'Something went wrong with autosuggest',
                    variant: 'error',
                    id: 'autosuggest_Error',
                },
            })
        },
    })

    const firmAutoSuggestOptions = useMemo(
        () =>
            firmAutosuggestData?.firmAutoSuggest?.result?.filter(
                (firmName) => !!firmName?.split('%|%')[0]?.slice(1, -1)
            ) ?? [],

        [firmAutosuggestData]
    )

    const callAutoSuggestQuery = useCallback(
        (value?: string) =>
            firmAutoSuggest({
                variables: {
                    input: {
                        firmAccountTypes: [UserType.BPM],
                        searchLimit: 50,
                        searchField: 'DisplayNameWithFirmName',
                        searchText: !!stripDoubleSpacesTrimStart(value)
                            ? stripDoubleSpacesTrimStart(value)
                            : 'a', //checking blank string and setting 'a' as default search text
                    },
                },
            }),
        [firmAutoSuggest]
    )

    const handleChange = useCallback(
        (_, value) => {
            setProductDialogState({
                ...defaultAddProductDialogState,
                type,
                firmName: value,
            })

            callAutoSuggestQuery(value)
            setSearchText(value)
        },
        [setProductDialogState, errors, defaultAddProductDialogState, type]
    )

    const handleInputChange = useCallback(
        (_, value) => {
            if (!!errors.firmName) {
                setProductDialogState({ errors: { ...errors, firmName: '' } })
            }
            callAutoSuggestQuery(value)

            setSearchText(stripDoubleSpacesTrimStart(value))
        },
        [setSearchText, callAutoSuggestQuery, errors, setProductDialogState]
    )

    const handleOpen = useCallback(() => {
        if (!!searchText) {
            callAutoSuggestQuery(searchText)
        }
    }, [callAutoSuggestQuery, searchText])

    useEffect(() => {
        callAutoSuggestQuery()
    }, [])

    return (
        <GridItem
            className={clsx(
                classes.rowContainer,
                classes.smallContainer,
                'rowContainer'
            )}
            xs={7}
            data-testid="firmName"
        >
            <AutoCompleteInput
                name="firmName"
                label="Company"
                valid={!errors.firmName || errors.firmName === ''}
                handleChange={handleChange}
                handleInputChange={handleInputChange}
                handleOpen={handleOpen}
                selectedValue={firmName}
                loading={!!firmAutoLoading}
                items={firmAutoSuggestOptions}
                getOptionLabel={(option: any) => getFirmName(option) ?? ''}
            />
        </GridItem>
    )
}

export default FirmNameAutoCompleteInput
